import * as React from 'react';

import { FieldProperty, FieldProps } from "./field.types";
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { generateId } from '@common/utils/common';
import { I18n } from 'react-redux-i18n';
import { QuestionnaireFieldType } from '@common/dto/questionnaire/field/type';
import { QuestionnaireSubfieldUpdateDto } from '@common/dto/questionnaire/field/subfield-update-dto';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { FieldType } from '@common/dto/field/type';
import { QuestionnaireFieldOptionListLocalDto } from '@modules/fhq-design-tool/dto/field-option';

export const useFieldController = (props: FieldProps) => {
  const { data, onChange, editableProperties } = props;

  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [fieldSkipLogicConditions, setFamilySkipLogicConditions] = React.useState<Array<SkipLogicCondition>>([]);
  const [dropdownOptions, setDropdownOptions] = React.useState<Array<QuestionnaireFieldOptionListLocalDto>>([]);

  React.useEffect(() => {
    setFamilySkipLogicConditions(data.skipLogic?.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition)) || []);
    setDropdownOptions((data.options || []).map((x) => ({ ...x, id: generateId() })));
  }, [getUniqueFieldCombination({ id: data.progenyId, context: data.context, type: data.type })]);

  const updateField = React.useCallback((field: Partial<QuestionnaireFieldUpdateDto | QuestionnaireSubfieldUpdateDto | QuestionnaireFieldAddDto>) => {
    onChange({
      ...data,
      ...field
    });
  }, [data, onChange]);

  const handleFieldNameChange = React.useCallback((val: string) => {
    updateField({ name: val.trim() });
  }, [updateField]);

  const handleFieldDescriptionChange = React.useCallback((val: string) => {
    updateField({ description: val });
  }, [updateField]);

  const handleFieldRequiredChange = React.useCallback((val: boolean) => {
    updateField({ required: val });
  }, [updateField]);

  const handleFieldTypeChange = React.useCallback((val: QuestionnaireFieldType) => {
    updateField({ type: val });
  }, [updateField]);

  const handleOptionsChange = React.useCallback((val: Array<QuestionnaireFieldOptionListLocalDto>) => {
    updateField({ options: val.map(({ id, ...origin }) => origin) });
    setDropdownOptions(val);
  }, [updateField]);

  const handleTableAllowDuplicationsChange = React.useCallback((val: boolean) => {
    updateField({ allowDuplications: val });
  }, [updateField]);

  const canDeleteTableField = React.useCallback(() => {
    return false;
  }, []);

  type FieldInList = {
    id: string;
    name: string;
    isVisible: boolean;
    original: QuestionnaireSubfieldUpdateDto;
  };

  const handleTableFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    updateField({
      subfields: newFieldsOrder.map((x, i) => ({
        ...x.original,
        order: i + 1,
      }))
    });
  }, [updateField]);

  const handleTableFieldShowHide = React.useCallback((field: FieldInList, isVisible: boolean) => {
    const { subfields = [] } = data as QuestionnaireFieldUpdateDto;

    updateField({
      subfields: subfields.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType }) 
          ? {
            ...field.original,
            displayInFHQ: isVisible,
          }
          : x
      ),
    });
  }, [updateField, data]);

  const navigateOnTableFieldEdit = React.useCallback((field: FieldInList) => {
    setEditingFieldUniqueCombination(getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType }));
  }, []);

  const goBackFromTableFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
  }, []);

  const handleSubfieldChange = React.useCallback((field: QuestionnaireSubfieldUpdateDto) => {
    const { subfields = [] } = data as QuestionnaireFieldUpdateDto;
    
    updateField({
      subfields: subfields.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType }) 
          ? field 
          : x
      ),
    });
  }, [updateField]);

  const handleUseSkipLogicChange = React.useCallback((val: string) => {
    let skipLogicEnabled = false;

    try {
      skipLogicEnabled = JSON.parse(val);
    } catch {}

    updateField({ skipLogicEnabled });
  }, [updateField]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions } : { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const skipLogic: Array<SkipLogicCondition> = [
      ...fieldSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition))
    ];

    updateField({
      skipLogic: skipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setFamilySkipLogicConditions(skipLogic);
  }, [updateField, fieldSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((skipLogic: SkipLogicCondition[]) => {
    updateField({
      skipLogic: skipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setFamilySkipLogicConditions(skipLogic);
  }, [updateField]);

  const fieldTypeChooserConfig = React.useMemo(() => {
    const config = [
      {
        allowedProgenyFieldTypes: [FieldType.text, FieldType.integer],
        options: [
          { value: QuestionnaireFieldType.text, label: I18n.t('Text') },
          { value: QuestionnaireFieldType.staticText, label: I18n.t('Static text') },
        ],
        label: I18n.t('Text Type'),
      },
      {
        allowedProgenyFieldTypes: [FieldType.checkbox],
        options: [
          { value: QuestionnaireFieldType.checkbox, label: I18n.t('Checkbox') },
          { value: QuestionnaireFieldType.toggle, label: I18n.t('Toggle') },
        ],
        label: I18n.t('Checkbox Type'),
      },
      {
        allowedProgenyFieldTypes: [FieldType.dropdown],
        options: [
          { value: QuestionnaireFieldType.dropdown, label: I18n.t('Dropdown') },
          { value: QuestionnaireFieldType.radio, label: I18n.t('Radio') },
          { value: QuestionnaireFieldType.searchDropdown, label: I18n.t('Search dropdown') },
        ],
        label: I18n.t('Dropdown Type'),
      }
    ];

    const configItem = config.find((x) => x.allowedProgenyFieldTypes.includes(data.progenyFieldType));

    return configItem;
  }, [data.progenyFieldType]);

  const editingSubfield = (data as QuestionnaireFieldUpdateDto).subfields?.find((x) => getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === editingFieldUniqueCombination);

  const propertyCanBeEdited = React.useCallback((property?: FieldProperty) => {
    if (!editableProperties) {
      return true;
    }

    if (!property) {
      return false;
    }

    return editableProperties.includes(property);
  }, [editableProperties]);

  const processedSubfields = React.useMemo(() => {
    return ((data as QuestionnaireFieldUpdateDto).subfields ?? [])
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        isVisible: x.displayInFHQ,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [(data as QuestionnaireFieldUpdateDto).subfields]);

  return {
    dropdownOptions,
    processedSubfields,
    propertyCanBeEdited,
    fieldTypeChooserConfig,
    handleOptionsChange,
    fieldSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    editingSubfield,
    goBackFromTableFieldEdit,
    handleSubfieldChange,
    navigateOnTableFieldEdit,
    handleTableFieldsOrderChange,
    handleTableFieldShowHide,
    canDeleteTableField,
    handleTableAllowDuplicationsChange,
    handleFieldTypeChange,
    handleFieldNameChange,
    handleFieldDescriptionChange,
    handleFieldRequiredChange,
  };
};